<template>
    <div v-clickaway="blur" :class="inputClass">
        <div class="form-input-select__wrapper">
            <div :class="valueClass" @click="toggle()">
                <div v-if="value && value.length" class="form-input-select__labels">
                    <div v-if="oneOptionSelected" class="form-input-select__option-label-name">
                        {{ lastSelectedOptionLabel }}
                    </div>

                    <common-labels :labels="selectedOptions" @click="toggleOption($event)"></common-labels>
                </div>

                <span v-else>
                    {{ placeholder }}
                </span>

                <common-icon v-if="isOpen" name="close" size="medium"></common-icon>
                <common-icon v-else name="dropdown-arrows" size="medium"></common-icon>
            </div>

            <div ref="options" class="form-input-select__options">
                <section v-for="(group, index) in multiSelectOptions" :key="`group-${index}}`">
                    <h5
                        v-if="group.heading"
                        class="form-input-select__options-heading"
                        :class="{ 'form-input-select__options-heading--is-open': isExpanded(group) }"
                        @click="toggleExpand(group)"
                    >
                        {{ group.heading }}

                        <common-icon v-if="expandable" name="menu-arrow" size="large"></common-icon>
                    </h5>

                    <button
                        v-for="(option, optionIndex) in group.options"
                        v-show="!expandable || isExpanded(group) || group.heading === null"
                        :key="`option-${optionIndex}`"
                        class="form-input-select__option"
                        :class="{ 'form-input-select__suboption': isExpanded(group) && group.heading }"
                        type="button"
                        tabindex="-1"
                    >
                        <checkbox-input
                            :label="option.label"
                            :hasBigTapArea="true"
                            :isDisabled="isDisabled"
                            :value="isChecked(option)"
                            @change="toggleOption(option)"
                        ></checkbox-input>
                    </button>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
    import FormInputMixin from 'mixins/FormInputMixin'
    import IndicateScrollMixin from 'mixins/IndicateScrollMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'

    export default {
        mixins: [FormInputMixin, IndicateScrollMixin],

        components: {
            CheckboxInput,
        },

        props: {
            expandable: { type: Boolean, default: false },
            placeholder: { type: String, default: undefined },
            value: { type: Array, default: () => [] },
            options: { type: Array, required: true },
            oneOptionSelected: { type: Boolean, default: false },
            showSelectAllOption: { type: Boolean, default: false },
        },

        data() {
            return {
                isOpen: false,
                expanded: [],
                lastSelectedOptionLabel: null,
                multiSelectOptions: [],
            }
        },

        computed: {
            inputClass() {
                return {
                    'form-input-select': true,
                    'form-input-select--multi': true,
                    'form-input-select--is-open': this.isOpen,
                    'form-input-select--is-invalid': this.isInvalid,
                    'form-input-select--is-disabled': this.isDisabled,
                }
            },

            valueClass() {
                return {
                    'form-input-select__input': true,
                    'form-input-select__input--has-no-value': !this.value || !this.value.length,
                }
            },

            selectedOptions() {
                return (
                    this.multiSelectOptions
                        .reduce((options, group) => options.concat(group.options), [])
                        .filter(({ value }) => this.value.includes(value)) || []
                )
            },
        },

        methods: {
            isChecked({ value }) {
                if (!this.value) {
                    return false
                } else if (Number.isInteger(value) || !this.expandable) {
                    return this.value.includes(value)
                }

                return this.areAllGroupOptionsChecked(value)
            },

            getGroupOptions(groupHeading) {
                const optionGroup = this.options.find((option) => option.heading === groupHeading)
                return optionGroup.options
            },

            areAllGroupOptionsChecked(groupHeading) {
                return this.getGroupOptions(groupHeading)
                    .map((option) => option.value)
                    .filter((option) => Number.isInteger(option))
                    .every((option) => this.value.includes(option))
            },

            uncheckAllGroupOptions(groupHeading) {
                const groupOptionIds = this.getGroupOptions(groupHeading).map((option) => option.value)
                const selectedOptionsWithoutSelectedGroup = this.value.filter((optionId) => !groupOptionIds.includes(optionId))

                this.change(selectedOptionsWithoutSelectedGroup)
            },

            checkAllGroupOptions(groupHeading) {
                const groupOptionIds = this.getGroupOptions(groupHeading)
                    .map((option) => option.value)
                    .filter((option) => Number.isInteger(option) && !this.value.includes(option))

                this.change(this.value.concat(groupOptionIds))
            },

            setLastSelectedOptionLabelByOptionId(optionId) {
                this.lastSelectedOptionLabel = this.multiSelectOptions.find((option) => option.value === optionId).label
            },

            setLastSelectedOptionLabelBySuboptionId(suboptionId) {
                const option = this.multiSelectOptions.find((option) => {
                    return option.options && option.options.find((suboption) => suboption.value === suboptionId)
                })
                if (option && option.label) {
                    this.lastSelectedOptionLabel = option.label
                }
            },

            toggleOption({ value: newValue, data } = {}) {
                if (newValue === undefined) {
                    return
                }

                if (!Number.isInteger(newValue) && this.expandable) {
                    if (this.areAllGroupOptionsChecked(newValue)) {
                        return this.uncheckAllGroupOptions(newValue)
                    }
                    return this.checkAllGroupOptions(newValue)
                }

                if (data && data.optionId) {
                    this.setLastSelectedOptionLabelByOptionId(data.optionId)
                }

                if (this.isChecked({ value: newValue })) {
                    return this.change((this.value || []).filter((existingValue) => existingValue !== newValue))
                }
                if (this.oneOptionSelected && this.value?.length > 0) {
                    if (!data || !this.checkIfExpandableOptionsFromSameOption(data)) {
                        return this.change([newValue])
                    }
                }

                if (this.value) {
                    this.change([...this.value, newValue])
                } else {
                    this.change([newValue])
                }
            },

            checkIfExpandableOptionsFromSameOption(expandableOption) {
                const selectedOption = this.multiSelectOptions.filter((option) => option.value === expandableOption.optionId)[0]
                const expandableOptions = selectedOption.options.map((option) => option.value)
                if (expandableOptions.includes(this.value[0])) {
                    return true
                }
                return false
            },

            blur() {
                this.isFocused = false
                this.close()
            },

            close() {
                this.isOpen = false
            },

            toggle() {
                if (this.isDisabled) {
                    return
                }

                this.isOpen = !this.isOpen
            },

            isExpanded(group) {
                return this.expanded.includes(group.value)
            },

            toggleExpand(group) {
                if (!this.expandable) {
                    return
                }

                if (!this.isExpanded(group)) {
                    this.expanded.push(group.value)
                } else {
                    this.expanded = this.expanded.filter((item) => item !== group.value)
                }
            },

            prefillMultiselectOptions() {
                this.multiSelectOptions = this.options
                if (!this.showSelectAllOption) {
                    return
                }

                this.multiSelectOptions.forEach((option) => {
                    if (option.options.length > 1) {
                        option.options.unshift({ label: 'All', value: option.heading })
                    }
                })
            },
            prefillLastSelectedOptionLabel() {
                if (this.value && this.value.length && this.oneOptionSelected) {
                    this.setLastSelectedOptionLabelBySuboptionId(this.value[this.value.length - 1])
                }
            },
        },

        watch: {
            options() {
                this.prefillMultiselectOptions()
                this.prefillLastSelectedOptionLabel()
            },
        },

        mounted() {
            this.prefillMultiselectOptions()

            //CHECK FOR PREFILLED OPTIONS LABEL
            this.prefillLastSelectedOptionLabel()
        },
    }
</script>
